/**
 * Tick generation utilities for chart axes.
 * Pure functions for generating axis ticks - no React dependencies.
 */

export interface GenerateTicksResult {
  ticks: number[];
}

export interface TimeAxisTick {
  timestamp: number;
  x: number;
  label: string;
  isVisible: boolean;
}

export interface ValueAxisTick {
  value: number;
  y: number;
  label: string;
}

export interface SecondaryLabel {
  x: number;
  label: string;
  timestamp: number;
}

export interface IndexedTick {
  index: number;
  timestamp: number;
}

import type { TimeFormatPrecision } from "./formatters";

/**
 * Determine secondary label type based on precision.
 * Secondary label provides context one level higher than primary:
 * - hour/minute precision -> show date
 * - day/month precision -> show year
 * - year precision -> no secondary label
 */
export function getSecondaryLabelType(
  precision: TimeFormatPrecision
): "date" | "year" | null {
  switch (precision) {
    case "minute":
    case "hour":
      return "date";
    case "day":
    case "month":
      return "year";
    case "year":
      return null;
    default:
      return null;
  }
}

/**
 * Format secondary label based on type.
 */
export function formatSecondaryLabel(
  timestamp: number,
  labelType: "date" | "year",
  locale: string
): string {
  const date = new Date(timestamp);

  try {
    if (labelType === "date") {
      return new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
      }).format(date);
    }

    if (labelType === "year") {
      return new Intl.DateTimeFormat(locale, { year: "numeric" }).format(date);
    }
  } catch {
    // Fallback for unsupported locales
  }

  if (labelType === "year") return date.getFullYear().toString();

  return `${date.getMonth() + 1}/${date.getDate()}`;
}

/**
 * Check if two timestamps are in different periods (for secondary label changes).
 */
export function isInDifferentPeriod(
  ts1: number,
  ts2: number,
  labelType: "date" | "year"
): boolean {
  const d1 = new Date(ts1);
  const d2 = new Date(ts2);

  if (labelType === "year") {
    return d1.getFullYear() !== d2.getFullYear();
  }

  return (
    d1.getFullYear() !== d2.getFullYear() ||
    d1.getMonth() !== d2.getMonth() ||
    d1.getDate() !== d2.getDate()
  );
}

/**
 * Compute secondary labels (date/year) centered in their respective period segments.
 */
export function computeSecondaryLabels(
  ticks: Array<{ timestamp: number; x: number }>,
  secondaryLabelType: "date" | "year" | null,
  chartLeft: number,
  chartRight: number,
  locale: string
): SecondaryLabel[] {
  if (!secondaryLabelType || ticks.length === 0) {
    return [];
  }

  const boundaryPositions: number[] = [];

  for (let i = 1; i < ticks.length; i++) {
    const prevTick = ticks[i - 1];
    const tick = ticks[i];

    if (
      prevTick &&
      tick &&
      isInDifferentPeriod(
        prevTick.timestamp,
        tick.timestamp,
        secondaryLabelType
      )
    ) {
      boundaryPositions.push((prevTick.x + tick.x) / 2);
    }
  }

  const segmentEdges = [chartLeft, ...boundaryPositions, chartRight];
  const labels: SecondaryLabel[] = [];

  for (let i = 0; i < segmentEdges.length - 1; i++) {
    const leftEdge = segmentEdges[i];
    const rightEdge = segmentEdges[i + 1];

    if (leftEdge === undefined || rightEdge === undefined) continue;

    const centerX = (leftEdge + rightEdge) / 2;

    const tickInSegment = ticks.find(
      (t) => t.x >= leftEdge && t.x <= rightEdge
    );

    if (tickInSegment) {
      labels.push({
        x: centerX,
        label: formatSecondaryLabel(
          tickInSegment.timestamp,
          secondaryLabelType,
          locale
        ),
        timestamp: tickInSegment.timestamp,
      });
    }
  }

  return labels;
}

const MAX_TICK_COUNT = 100;

/**
 * Round a value to a "nice" number (1, 2, 2.5, or 5 x 10^n).
 */
function niceNum(value: number, round: boolean): number {
  if (value === 0) return 1;

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const fraction = Math.abs(value) / Math.pow(10, exponent);
  let niceFraction: number;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 2.25) niceFraction = 2;
    else if (fraction < 3.5) niceFraction = 2.5;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 2.5) niceFraction = 2.5;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;

  return niceFraction * Math.pow(10, exponent);
}

/**
 * Generate human-readable "nice" tick values for a numeric axis.
 * Expands the domain to align with nice step boundaries (1, 2, 2.5, 5 multiples).
 */
export function generateNiceYTicks(
  domain: [number, number],
  maxTicks: number
): number[] {
  const [min, max] = domain;
  const range = max - min;

  if (range === 0) return [min];
  if (maxTicks <= 1) return [min];

  const niceStep = niceNum(range / (maxTicks - 1), true);
  const startIndex = Math.floor(min / niceStep);
  const endIndex = Math.ceil(max / niceStep);

  const ticks: number[] = [];

  for (
    let i = startIndex;
    i <= endIndex && ticks.length < MAX_TICK_COUNT;
    i++
  ) {
    ticks.push(i * niceStep);
  }

  return ticks;
}

/**
 * Generate tick timestamps by evenly dividing the data array by index.
 * Picks tickCount data points at equally-spaced indices through the array.
 * Viewport-aware: pass only visible data for viewport-scoped ticks.
 */
export function generateIndexEquidistantTicks(
  data: { timestamp: number }[],
  tickCount: number
): number[] {
  if (data.length === 0 || tickCount <= 0) return [];

  const first = data[0];

  if (data.length === 1) return first ? [first.timestamp] : [];

  const step = (data.length - 1) / (tickCount + 1);
  const ticks: number[] = [];

  for (let i = 1; i <= tickCount; i++) {
    const index = Math.round(i * step);
    const point = data[index];

    if (point) ticks.push(point.timestamp);
  }

  return [...new Set(ticks)];
}
